<template>
  <div class="calendar-wrapper">
    <filters
      v-model="filters"
      :meeting-types="categories"
      @apply="fetchCalendar"
    />
    <controls
      v-model="currentDate"
      :categories="categories"
      :mode="filters.mode"
      @select-category="fetchCalendar"
    />

    <div v-if="isToday || isDay">
      <mode-day
        :value="appointments"
        :categories="categories"
        :loading="loading"
        @select-appointment="$emit('select-appointment', $event)"
      />
    </div>
    <div v-else-if="isWeek">
      <mode-week
        :value="appointments"
        :categories="categories"
        :current-date="currentDate"
        @select-appointment="$emit('select-appointment', $event)"
      />
    </div>
    <div v-else>
      <mode-month
        :value="appointments"
        :categories="categories"
        :current-date="currentDate"
        @select-day="openDayFromMonth"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import Filters from './Filters.vue';
import Controls from './Controls.vue';
import ModeDay from './calendar-modes/Day.vue';
import ModeWeek from './calendar-modes/Week.vue';
import ModeMonth from './calendar-modes/Month.vue';
import compuAppointmentService from '@/modules/compu-appointment/api';

export default {
  name: 'CompuCalendar',
  props: {
    categories: { type: Array, default: () => [] },
  },
  components: {
    Filters,
    Controls,
    ModeDay,
    ModeWeek,
    ModeMonth,
  },
  data() {
    return {
      appointments: [],
      loading: false,
      filters: {
        mode: 'day',
        actions: [],
        academicYear: null,
        searchText: '',
        status: null,
        meetingType: null,
      },
      currentDate: moment()
        .startOf('day')
        .toDate(),
      restoringState: false,
      changingView: false,
    };
  },
  computed: {
    isToday() {
      return this.filters.mode === 'today';
    },
    isDay() {
      return this.filters.mode === 'day';
    },
    isWeek() {
      return this.filters.mode === 'week';
    },
    isMonth() {
      return this.filters.mode === 'month';
    },
  },
  watch: {
    currentDate() {
      if (this.restoringState || this.changingView) {
        return;
      }

      this.fetchCalendar();
    },
    'filters.mode'() {
      if (this.restoringState || this.changingView) {
        return;
      }

      if (this.isToday) {
        this.currentDate = moment()
          .startOf('day')
          .toDate();
      }

      this.fetchCalendar();
    },
  },
  mounted() {
    this.fetchCalendar();
  },
  methods: {
    getCalendarState() {
      return {
        date: moment(this.currentDate).format('YYYY-MM-DD'),
        mode: this.filters.mode,
        selectedCategoryIds: this.categories
          .filter((category) => category.selected)
          .map((category) => Number(category.id)),
        calendarFilters: {
          academicYear: this.filters.academicYear,
          searchText: this.filters.searchText,
          status: this.filters.status,
          meetingType: this.filters.meetingType,
        },
      };
    },
    restoreCalendarState(state) {
      if (!state) {
        return;
      }

      const selectedCategoryIds = (state.selectedCategoryIds || []).map(
        (id) => Number(id)
      );
      const nextDate = moment(state.date);

      this.restoringState = true;

      this.categories.forEach((category) => {
        this.$set(
          category,
          'selected',
          selectedCategoryIds.includes(Number(category.id))
        );
      });

      this.filters.mode = state.mode || 'day';
      this.filters.academicYear = state.calendarFilters?.academicYear || null;
      this.filters.searchText = state.calendarFilters?.searchText || '';
      this.filters.status = state.calendarFilters?.status || null;
      this.filters.meetingType = state.calendarFilters?.meetingType || null;
      this.currentDate = (nextDate.isValid() ? nextDate : moment())
        .startOf('day')
        .toDate();

      this.$nextTick(() => {
        this.restoringState = false;
        this.fetchCalendar();
      });
    },
    openDayFromMonth(date) {
      this.changingView = true;
      this.currentDate = moment(date)
        .startOf('day')
        .toDate();
      this.filters.mode = 'day';

      this.$nextTick(() => {
        this.changingView = false;
        this.fetchCalendar();
      });
    },
    async fetchCalendar() {
      try {
        this.loading = true;

        const selectedCategoryIds = this.categories
          .filter((category) => category.selected)
          .map((category) => category.id);
        const selectedMeetingTypeIds = this.filters.meetingType
          ? [this.filters.meetingType.id]
          : selectedCategoryIds;

        const response = await compuAppointmentService.fetchCalendar({
          date: moment(this.currentDate).format('YYYY-MM-DD'),
          viewMode: this.isToday ? 'day' : this.filters.mode,
          meetingTypeIds: selectedMeetingTypeIds,
          statusId: this.filters.status?.id || null,
          searchText: this.filters.searchText || null,
        });

        this.appointments = response.items || response.Items || [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped></style>
